/**
 * Fractional indexing utility for drag-and-drop reordering
 * Uses large integer increments to ensure integer positions
 */

const defaultIncrement = 1000; // Space between positions
const minGap = defaultIncrement * 0.001; // When positions get too close, we'll need to reindex

/**
 * Generate a position between two positions
 * @param before - Position of the item before (null if inserting at start)
 * @param after - Position of the item after (null if appending at end)
 * @returns New position value
 */
export function getPositionBetween(
  before: number | null,
  after: number | null,
): number {
  // Empty list - first item ever
  if (before == null && after == null) return defaultIncrement;
  // Inserting before the first item
  if (before == null) return after! - defaultIncrement;
  // Appending after the last item
  if (after == null) return before + defaultIncrement;
  // Inserting between two existing items
  return (before + after) / 2;
}

/**
 * Reindex all items to have evenly spaced positions
 * Call this when positions get too close together
 */
export function reindexPositions<T extends { id: string }>(
  items: T[],
  increment = defaultIncrement,
): (T & { position: number })[] {
  return items.map((item, idx) => ({
    ...item,
    position: (idx + 1) * increment,
  }));
}

/**
 * Check if reindexing is needed. Required the list to be sorted by position.
 */
export function needsReindexing(positions: number[]): boolean {
  return positions.some((pos, i) => i > 0 && pos - positions[i - 1] <= minGap);
}
